import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { Play, Pause, CheckSquare, Wrench } from "@phosphor-icons/react";
import { Button } from "@/components/ui/button";
import { api, formatApiError, fmtDateTime, offlineSafe } from "@/lib/api";

export default function MyJobs() {
  const [items, setItems] = useState([]);

  const load = () => api.get("/tasks/my").then((r) => setItems(r.data));
  useEffect(() => { load(); }, []);

  const update = async (tid, patch) => {
    try {
      const res = await offlineSafe("patch", `/tasks/${tid}`, patch, `Task ${tid}`);
      if (res?._queued) toast.info("Saved on device — will sync when back online");
      load();
    }
    catch (e) { toast.error(formatApiError(e)); }
  };

  const pending = items.filter((t) => t.status !== "done");
  const done = items.filter((t) => t.status === "done");

  return (
    <div className="space-y-6" data-testid="myjobs-root">
      <div>
        <div className="text-xs uppercase font-mono tracking-widest text-strawberry mb-1">Shop Floor</div>
        <h1 className="text-3xl font-display font-bold">My Jobs</h1>
        <p className="text-sm text-steel mt-2">Tasks assigned to you. Big buttons — tap through with gloves on.</p>
      </div>

      <div className="grid md:grid-cols-2 gap-4">
        {pending.length === 0 && (
          <div className="md:col-span-2 p-10 text-center bg-white dark:bg-white border border-frosted/50 dark:border-frosted/50">
            <Wrench size={40} className="mx-auto text-steel/80 mb-3" weight="duotone" />
            <div className="font-display text-lg">Nothing on your bay right now</div>
            <div className="text-sm text-steel">New tasks assigned to you will appear here.</div>
          </div>
        )}
        {pending.map((t) => (
          <div key={t.id} data-testid={`myjob-card-${t.id}`} className="p-5 bg-white dark:bg-white border border-frosted/50 dark:border-frosted/50">
            <div className="flex items-start justify-between">
              <div>
                <Link to={`/job-cards/${t.job_card_id}`} className="font-mono text-strawberry text-sm hover:underline">
                  {t.job_number}
                </Link>
                <div className="font-display text-lg font-semibold mt-1">{t.task_name}</div>
                {t.vehicle && (
                  <div className="mt-1 text-sm text-steel font-mono">
                    {t.vehicle.reg_number} · {t.vehicle.make} {t.vehicle.model}
                  </div>
                )}
              </div>
              <span className={`inline-block text-[10px] uppercase font-mono tracking-widest px-2 py-1 border ${
                t.status === "in_progress" ? "bg-strawberry/15 text-strawberry border-strawberry/30" :
                t.status === "paused" ? "bg-blue-500/15 text-blue-500 border-blue-500/30" :
                "bg-zinc-500/15 text-steel border-zinc-500/30"
              }`}>{t.status.replace("_", " ")}</span>
            </div>

            <div className="mt-4 grid grid-cols-3 gap-2">
              {t.status !== "in_progress" && (
                <Button data-testid={`myjob-start-${t.id}`} onClick={() => update(t.id, { status: "in_progress" })}
                  className="h-12 btn-primary-brand rounded-md text-white font-semibold press-scale">
                  <Play size={18} weight="fill" /> Start
                </Button>
              )}
              {t.status === "in_progress" && (
                <Button data-testid={`myjob-pause-${t.id}`} onClick={() => update(t.id, { status: "paused" })}
                  className="h-12 rounded-none bg-steel hover:bg-steel text-white font-semibold press-scale">
                  <Pause size={18} weight="fill" /> Pause
                </Button>
              )}
              <Button data-testid={`myjob-complete-${t.id}`} onClick={() => update(t.id, { status: "done", hours_actual: t.hours_est })}
                className="h-12 col-span-2 rounded-none bg-emerald-600 hover:bg-emerald-500 text-white font-semibold press-scale">
                <CheckSquare size={18} weight="fill" /> Complete
              </Button>
            </div>
            <div className="mt-3 text-xs font-mono text-steel">
              {t.started_at ? `Started ${fmtDateTime(t.started_at)}` : "Not started"}
            </div>
          </div>
        ))}
      </div>

      {done.length > 0 && (
        <div className="bg-white dark:bg-white border border-frosted/50 dark:border-frosted/50">
          <div className="p-4 border-b border-frosted/50 dark:border-frosted/50 font-display font-semibold">Completed today ({done.length})</div>
          <div className="divide-y divide-zinc-200 dark:divide-zinc-800">
            {done.map((t) => (
              <div key={t.id} className="p-4 flex items-center justify-between text-sm">
                <div>
                  <div className="font-medium">{t.task_name}</div>
                  <div className="text-xs text-steel font-mono">{t.vehicle?.reg_number} · {t.job_number}</div>
                </div>
                <div className="text-xs font-mono text-emerald-500">✓ done · {fmtDateTime(t.completed_at)}</div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
